import { cn } from "lib/utils";
import { Add, Trash } from "iconsax-reactjs";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import AssignSupplier from "component/flex/AssignSupplier";
import { useSelector } from "react-redux";
import Input from "component/form/Input";
import PreviewInvoice from "./PreviewInvoice";
import currencyPairCheck from "utilities/currencyPairCheck";
import { InfoCircleIcon } from "asset/icons";
import ConfirmInvoice from "component/confirmInvoice";
import Loader from "component/loader";
import { useMutation, useQueryClient } from "react-query";
import useGetRequest from "hooks/useGetRequest";
import MoreOptionsDropdown from "component/flex/shipments/modals/MoreOptionsDropdown";
import Loading from "component/loading";
import { handleCreateInvoice } from "api/fetchers";
import CurrencyToggle from "component/currencyToggle";
import { formatMoney } from "utilities/formatMoney";
import { errorAlert } from "utilities/alerts";
import { toast } from "react-toastify";
import AddPoPaymentInfoModal from "component/flex/AddPoPaymentInfoModal";
import { addOrUpdateUser } from "api/credit-fetchers";
import useUserDetails from "hooks/useUserDetails";

export const invoiceItemAmountCalculation = (item, currency) => {
  const calculatedItemAmount =
    currency.length > 1
      ? currencyPairCheck(currency) +
      formatMoney(
        parseFloat(
          item.pricePerUnit *
          item.quantity.toLocaleString("en-US", {
            minimumFractionDigits: 2,
          })
        )
      )
      : currency +
      formatMoney(
        parseFloat(
          item.pricePerUnit *
          item.quantity.toLocaleString("en-US", {
            minimumFractionDigits: 2,
          })
        )
      );

  return calculatedItemAmount;
};

const CreateInvoiceV2 = () => {
  const { business, users: user, currency: userSelectedCurrency, isLogin } = useSelector((state) => state);
  const { transferFeesData,
    isLoadingTransferFees, newWalletsData, userWallets, userWalletsAccounts } = useUserDetails({
      enableTransferFees: true,
    })
  const [fetchSuppliers, supps, loadingSupplier] = useGetRequest();
  const [showPreview, setShowPreview] = useState(false);
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const invoiceIdToEdit = searchParams.get("invoiceIdToEdit");
  const { isEdit, invoice, isDuplicating } = state || {};
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [issueDate, setIssueDate] = useState();
  const [dueDate, setDueDate] = useState();
  const currency = isLogin ? userSelectedCurrency : "NGN";
  const [showModal3, setShowModal3] = useState(false);
  const [loader, setloader] = useState(false);
  const [note, setNote] = useState("");
  const [addTax, setAddTax] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const [showPaymentInfoModal, setShowPaymentInfoModal] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [accountName, setAccountName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const queryClient = useQueryClient();
  const [items, setItems] = useState([
    {
      itemName: "",
      quantity: 0,
      pricePerUnit: 0,
    },
  ]);

  const selectedCurrency = String(currency || "NGN").toUpperCase();

  const taxRate = useMemo(() => (transferFeesData?.taxValue), [selectedCurrency, isLoadingTransferFees]);

  const taxPercentLabel = useMemo(() => {
    const convertedTaxRate = taxRate < 1 ? (taxRate * 100) : (taxRate);
    return isLoadingTransferFees ? 0 : convertedTaxRate;
  }, [taxRate, isLoadingTransferFees]);

  const navigate = useNavigate();

  const invoiceDropDownOptions = (invoice) => {
    return [
      {
        label: showPreview ? "Hide Preview" : "Show Preview",
        value: "hide-preview",
        action: async () => {
          setShowPreview((prev) => !prev);
        },
      },
      {
        label: "Submit Draft",
        value: "submit-draft",
        action: () => {
          goSubmitInvoice(submissionType[0]);
        },
      },
    ];
  };

  const { mutateAsync: handleUpdateUserDetails, isLoading: isUpdatingUser } = useMutation({
    mutationFn: (data) => addOrUpdateUser(data),
    mutationKey: ["add_user_to_network"],
    onSuccess: (response) => {
      if (response?.response?.data?.statusCode !== 400) {
        toast.success(response?.data?.message || "Flex user updated successfully");
        setAccountNumber("");
        setAccountName("");
        setSelectedBank(null);
        setShowPaymentInfoModal(false);
      } else {
        toast.error(response?.response?.data?.message);
      }
    },
  });


  const validateAllItemsTitle = items.every(
    (item) => item?.itemName?.length && item?.quantity?.length && item?.pricePerUnit?.length
  );
  const updateItemItems = (e, indexs) => {
    const newState = items.map((obj, index) => {
      if (index === indexs) {
        return {
          ...obj,
          itemName: e.target.value,
        };
      }
      return obj;
    });

    setItems(newState);
  };
  const isValidDate = (date) => date instanceof Date && !isNaN(date);

  const parsedIssueDate = new Date(issueDate);
  const parsedDueDate = new Date(dueDate);

  const isSelectedDateValid =
    isValidDate(parsedIssueDate) &&
    isValidDate(parsedDueDate) &&
    parsedIssueDate.getTime() <= parsedDueDate.getTime();
  const goBack = () => navigate(-1);
  const submissionType = ["DRAFT", "SUBMIT"];
  const { mutateAsync: handleSubmitInvoice } = useMutation({
    mutationFn: async (status = submissionType[0]) => {
      const data = {
        currency: currency,
        issueDate: issueDate,
        dueDate: dueDate,
        orderItems: items,
        flexUser: selectedSupplier?._id,
        note,
        status,
        tax: addTax,
      };
      if (isEdit && invoiceIdToEdit) {
        data["invoiceId"] = invoiceIdToEdit;
      }

      if (!validateAllItemsTitle && isDraft) {
        delete data["orderItems"];
      }
      if (isDraft) {
        for (let item in data) {
          if (data[item] === undefined || data[item] === "") {
            delete data[item];
          }
        }
      }
      if (isDuplicating) {
        delete data["invoiceId"];
      }
      return handleCreateInvoice(data);
    },
    mutationKey: "createInvoice",
    onSuccess: async (data) => {
      setloader(false);
      setIsDraft(false);
      if (data?.statusCode === 400) {
        toast.error(data?.message?.[0] || data?.message || "An error occurred");
        return;
      }
      if (data?.error === false) {
        navigate(`/flex/success`, {
          state: {
            ...data,
          },
        });
      }
    },
    onError: (e) => {
      setloader(false);
      setIsDraft(false);
      toast.error(e?.response?.data ? e?.response?.data?.message : "An error occurred");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["invoiceSummary", business?._id, currency],
      });
    },
  });

  const updateItemQuantity = (e, id, p) => {
    const newState = items.map((obj, index) => {
      if (index === id) {
        return {
          ...obj,
          quantity: e.target.value,
        };
      }
      return obj;
    });

    setItems(newState);
  };

  const updateItemPrice = (e, indexs, q) => {
    const newState = items.map((obj, index) => {
      if (index === indexs) {
        return {
          ...obj,
          pricePerUnit: e.target.value,
        };
      }
      return obj;
    });

    setItems(newState);
  };

  const addAnotherSection = () => {
    const data = {
      itemName: "",
      quantity: 0,
      pricePerUnit: 0,
    };
    setItems((current) => [...current, data]);
  };

  const secondSubtotal = items.reduce((a, v) => (a = a + v.pricePerUnit * v.quantity), 0);

  const totalAmount = items.reduce((userSelectedCurrency, item) => {
    return (userSelectedCurrency = parseInt(userSelectedCurrency) + parseInt(item.pricePerUnit * item.quantity));
  }, 0);

  const minimumAmountBaseOnCurrency = {
    GBP: 1,
    NGN: 9000,
    USD: 1,
  };

  const MINIMUM_AMOUNT = minimumAmountBaseOnCurrency[currency] || 5;
  const isValidAmount = MINIMUM_AMOUNT < totalAmount;
  const deleteItems = (index) => {
    setItems((prev) => prev.filter((item, id) => id !== index));
  };
  const now = new Date();
  const todayStartOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

  const issueDateValue = new Date(issueDate).getTime();
  const dueDateValue = new Date(dueDate).getTime();

  const isIssueDateValid = issueDateValue >= todayStartOfDay;

  const isDueDateValid = dueDateValue >= issueDateValue;

  const areDatesValidForDuplication = isIssueDateValid && isDueDateValid;
  const isDisabled = Boolean(
    isSelectedDateValid && selectedSupplier && isValidAmount && validateAllItemsTitle
  );
  const hasSufficientDataForDraft = Boolean(
    isSelectedDateValid || selectedSupplier || isValidAmount || validateAllItemsTitle
  );
  const goSubmitInvoice = (status) => {
    if (isDuplicating && !areDatesValidForDuplication) {
      return errorAlert("Issued date and Due date must be from today");
    }
    if (status === submissionType[0]) {
      setIsDraft(true);
      return setShowModal3(true);
    } else {
      if (currency === "NGN" && secondSubtotal < MINIMUM_AMOUNT) {
        return errorAlert("Total amount of items should be more than or equal to ₦10,000");
      } else {
        return setShowModal3(true);
      }
    }
  };

  const showOnlyForm = showPreview ? "md:w-[55%]" : "md:w-[50%]";
  const normalWidth = "w-full md:w-11/12 mt-5";

  const theSelectedCurrency = currencyPairCheck(currency);
  const itemAmountCalculation = (item) => {
    return (
      theSelectedCurrency +
      formatMoney(
        parseFloat(
          item.pricePerUnit *
          item.quantity.toLocaleString("en-US", {
            minimumFractionDigits: 2,
          })
        )
      )
    );
  };

  const subTotal = theSelectedCurrency + parseFloat(secondSubtotal).toLocaleString();
  const taxAmountNumber = taxRate < 1 ? taxRate * secondSubtotal : (taxRate / 100) * secondSubtotal;
  const taxedTotal = isNaN(taxAmountNumber) ? theSelectedCurrency + '0' : theSelectedCurrency + parseFloat(taxAmountNumber).toLocaleString();
  const totalNumber = addTax ? secondSubtotal + taxAmountNumber : secondSubtotal;

  const total = theSelectedCurrency + parseFloat(totalNumber).toLocaleString();

  const validateForDuplicate = isDuplicating
    ? areDatesValidForDuplication && isDisabled
    : isDisabled;


  const handleFetchSuppliers = async (businessId) => {
    await fetchSuppliers("/v1/flex/users", {
      headers: { "x-business-id": businessId },
    });
  };
  useEffect(() => {
    if (business?._id && (isEdit || isDuplicating)) {
      handleFetchSuppliers(business?._id);
    }
  }, []);

  const fetchSuppliersAndUpdateDataFunc = async () => {
    setIssueDate(invoice?.issueDate);
    invoice?.due ? setDueDate(invoice?.due) : setDueDate(invoice?.dueDate);
    setItems(invoice?.orderItems || []);
    setNote(invoice?.note || "");
    setAddTax(invoice?.tax);
    setShowPreview(true);
  };
  useEffect(() => {
    if (business?._id && (isEdit || isDuplicating) && invoice) {
      fetchSuppliersAndUpdateDataFunc();
    }
  }, []);

  const nameToUse = (supplier) =>
    supplier?.business?._id === business?._id
      ? supplier?.businessName?.toLowerCase()
      : supplier?.business?.name?.toLowerCase();
  function findMatchingCustomer(dataArray, orderObject) {
    return dataArray.find((item) => {
      return (
        item?.business?.name === orderObject?.customer ||
        item?.business?._id === orderObject?.business
      );
    });
  }

  useEffect(() => {
    if ((isEdit || isDuplicating) && supps?.length) {
      const findSupplier = findMatchingCustomer(supps, invoice);

      if (findSupplier) {
        setSelectedSupplier({
          name: nameToUse(findSupplier),
          _id: findSupplier?._id,
          personalName: `${findSupplier?.firstName ?? ""} ${findSupplier?.lastName ?? ""
            }`,
          ...findSupplier,
        });
      }
    }
  }, [supps?.length, isEdit, isDuplicating]);

  const isBusinessApproved = business?.status === "APPROVED";
  const calendarMinDate = new Date().toISOString().split("T")[0];

  return (
    <>
      {(isEdit || isDuplicating) && loadingSupplier && (
        <div className="fixed left-0 top-0 h-screen w-screen z-[1000000] opacity-65">
          <Loading />
        </div>
      )}
      <main className="w-full overflow-hidden bg-bg-color-10">
        <div className="max-w-[2000px] mx-auto w-screen ">
          <nav className="flex items-center justify-between w-[96%]  py-3 my-0 md:w-full px-2 md:px-4 myborder-slixe mx-auto">
            <div className="flex items-center gap-1 md:gap-3">
              <Add className="rotate-45 cursor-pointer" onClick={goBack} />

              <h6 className="m-0 text-gray-90 text-base md:text-xl font-[450]">
                {isEdit ? "Edit" : isDuplicating ? "Duplicate" : "Create"} invoice
              </h6>
            </div>

            <div className="flex items-center gap-4">
              <div className="md:hidden">
                <MoreOptionsDropdown
                  options={invoiceDropDownOptions(invoice)}
                  horizontal
                />
              </div>
              <button
                onClick={() => {
                  goSubmitInvoice(submissionType[0]);
                }}
                type="button"
                disabled={!hasSufficientDataForDraft}
                className={cn(
                  `hover:opacity-90 hidden  !bg-transparent mt-0 sm:!mt-0 md:flex items-center gap-2 !text-main-dark rounded-lg py-2 px-2 md:px-4 text-sm`,
                  !hasSufficientDataForDraft && "opacity-50 cursor-not-allowed"
                )}
              >
                Save as draft
              </button>
              <button
                onClick={() => setShowPreview((prev) => !prev)}
                className={`hover:opacity-90 !bg-secondary-10 hidden mt-0 sm:!mt-0 md:flex items-center gap-2 !text-secondary rounded-lg py-2 px-2 md:px-4 text-sm`}
              >
                {!showPreview ? "Show" : "Hide"} Preview
              </button>
              <button
                onClick={() => goSubmitInvoice()}
                disabled={!validateForDuplicate}
                className={`${!validateForDuplicate
                  ? "bg-[#EBE4FF] text-[#BFA6FF]"
                  : "bg-[#6F00FF] text-[#ffff]"
                  } hover:opacity-90  mt-0 sm:!mt-0 flex items-center gap-2  rounded-lg py-2 px-2 md:px-4 text-xs md:text-sm`}
              >
                Save changes
              </button>
            </div>
          </nav>

          <section
            className={cn(
              `flex items-start flex-col md:flex-row justify-between w-11/12 mx-auto my-5 gap-6`,
              showPreview ? "justify-between" : "justify-center"
            )}
          >
            <div className={cn("w-full pb-10", showOnlyForm)}>
              <h6 className="text-main-dark font-[450] text-xl mb-3">
                {isEdit ? "Edit" : isDuplicating ? "Duplicate" : "Create new"}{" "}
                invoice
              </h6>

              <div className={cn(normalWidth)}>
                <AssignSupplier
                  business={business}
                  selectedSupplier={selectedSupplier}
                  setSelectedSupplier={setSelectedSupplier}
                  type={"buyer"}
                  label="Bill to"
                  subLabel="Add customer"
                  showPaymentInfoModal={showPaymentInfoModal}
                  setShowPaymentInfoModal={setShowPaymentInfoModal}
                />
              </div>
              <div className={cn(normalWidth, "mt-3")}>
                <label className="text-gray-70 font-[450] text-sm mb-2">Date</label>
                <div className="flex gap-4">
                  <Input
                    type="date"
                    placeholder="Issue date"
                    className="!text-base bg-secondary-30"
                    value={issueDate}
                    min={calendarMinDate}
                    onChange={setIssueDate}
                  />
                  <Input
                    type="date"
                    placeholder="Due date"
                    className="!text-base bg-secondary-30"
                    value={dueDate}
                    onChange={setDueDate}
                    min={issueDate}
                  />
                </div>
              </div>
              <div className={cn(normalWidth, "mt-3")}>
                <label className="text-gray-70 font-[450] text-sm mb-2">
                  Currency
                </label>

                <div className="flex gap-2">
                  <CurrencyToggle
                    className="relative flex items-center justify-between w-full p-3 text-sm font-medium border-0 rounded-lg outline-none cursor-pointer bg-secondary-30"
                    dropDownClassName="shadow-dropdown2 !left-0 !top-[109%]"
                    isDisabled={!isBusinessApproved}
                    fetchWallet
                    parentClassName="w-full"
                    isTransparent={true}
                    businessId={business?._id}
                  />
                </div>
              </div>
              <div
                className={cn(normalWidth, "mt-3 w-full gap-2 flex flex-col order")}
              >
                {items.map((item, index) => (
                  <div key={index} className="flex justify-between w-full gap-2">
                    <div className="flex justify-between w-full gap-2">
                      <div className="flex gap-2 flex-col w-[250px]">
                        {!index ? (
                          <label className="text-gray-70 font-[450] text-sm">
                            Items
                          </label>
                        ) : null}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            required
                            value={item?.itemName}
                            onChange={(e) => updateItemItems(e, index)}
                            className="customInput !bg-secondary-30"
                            placeholder="Add item name"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 flex-col w-[120px]">
                        {!index ? (
                          <label className="text-gray-70 font-[450] text-sm">
                            Quantity
                          </label>
                        ) : null}
                        <div className="flex gap-2">
                          <input
                            type="number"
                            required
                            value={item.quantity || ""}
                            onChange={(e) =>
                              updateItemQuantity(
                                e,
                                index,
                                item.pricePerUnit
                              )
                            }
                            className="customInput !bg-secondary-30"
                            placeholder="1"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 flex-col max-w-[140px] w-full">
                        {!index ? (
                          <label className="text-gray-70 font-[450] text-sm">
                            Unit price
                          </label>
                        ) : null}
                        <div className="flex gap-2">
                          <input
                            type="number"
                            onChange={(e) =>
                              updateItemPrice(e, index, item.quantity)
                            }
                            value={item.pricePerUnit || ""}
                            required
                            className="customInput !bg-secondary-30"
                            placeholder="Unit price"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 flex-col w-[160px] ">
                        {!index ? (
                          <label className="text-gray-70 font-[450] text-sm">
                            Amount
                          </label>
                        ) : null}
                        <div className="flex items-center h-[54px] p-2 gap-2 rounded-lg bg-[#F3F4FB]">
                          <div className="w-full px-0 py-2 text-base border-0 rounded-lg outline-0">
                            {itemAmountCalculation(item)}
                          </div>
                        </div>
                      </div>
                    </div>
                    {items.length > 1 && (
                      <div className="pb-[15px] justify-center w-1/12 flex items-end px-0">
                        <Trash
                          onClick={() => {
                            deleteItems(index);
                          }}
                          color="#6F00FF"
                          className="!text-bg-secondary"
                          size={18}
                        />
                      </div>
                    )}{" "}
                  </div>
                ))}
              </div>

              <div className={cn(normalWidth, "mt-3 flex gap-x-10 items-center")}>
                <button
                  className="flex text-[#6F00FF] outline-0 bg-none font-medium text-[14px] leading-5 tracking-[2%] items-center gap-2 cursor-pointer"
                  onClick={addAnotherSection}
                >
                  <Add className="!text-secondary" />
                  Add another item
                </button>
                {/* <label className="flex text-main-dark relative font-medium text-[14px] leading-5 tracking-[2%] items-center gap-2 cursor-pointer">
                                    Upload items from file
                                    <input className="absolute opacity-0" type="file" />
                                </label> */}
              </div>
              <div
                style={{
                  borderBlock: "1px dashed",
                }}
                className={cn(normalWidth, "mt-3 !border-secondary-60  py-3")}
              >
                <div className="flex flex-col ml-auto max-w-[384px] w-full gap-3">
                  <div className="flex justify-end gap-4">
                    <div className="text-base font-light text-center text-secondary-40">
                      Subtotal:
                    </div>
                    <div className="text-base font-medium text-center text-gray-40">
                      {subTotal}
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2 py-0 pr-0">
                    <div>
                      <input
                        onChange={(e) => {
                          setAddTax(e.target.checked);
                        }}
                        checked={addTax}
                        type="checkbox"
                        style={{
                          accentColor: "#6F00FF",
                          borderColor: "#6F739B",
                          borderRadius: "16px",
                          clipPath: "circle(100% at 50% 50%)",
                        }}
                      />
                    </div>
                    <div className="text-base font-light text-center text-secondary-40">
                      <div>Tax ({taxPercentLabel}%):</div>
                    </div>
                    <div className="text-base font-medium text-center text-gray-40">
                      <span style={{ marginLeft: "10px" }}>{taxedTotal}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className={cn(normalWidth, "mt-3")}>
                <div className="flex justify-end gap-4">
                  <p className="text-base font-light text-center text-secondary-40">
                    Total:
                  </p>
                  <p
                    style={{
                      color: !isValidAmount ? "crimson" : "#101828",
                      lineHeight: "24.3px",
                      fontSize: "18px",
                      fontWeight: "500",
                    }}
                    className="flex items-center justify-start gap-2"
                  >
                    {total}{" "}
                    {!isValidAmount && (
                      <span
                        title={`Total must be a minimum of ${theSelectedCurrency}10000`}
                      >
                        <InfoCircleIcon />
                      </span>
                    )}{" "}
                  </p>
                </div>
              </div>

              <div className={cn(normalWidth, "mt-4")}>
                <div className="flex flex-col gap-2">
                  <label className="text-gray-70 font-[450] text-sm">Notes</label>
                  <div className="gap-2">
                    <textarea
                      type="text"
                      name=""
                      id=""
                      onChange={(e) => {
                        setNote(e.target.value);
                      }}
                      value={note}
                      className="customInput !bg-secondary-30 !h-[96px]"
                      placeholder="Write a note here"
                    ></textarea>
                  </div>
                </div>
              </div>
              <div className={cn(normalWidth, "mt-4")}>
                <div className="flex items-center justify-end my-4">
                  {/* <div>
                                        <p className="text-base font-light">
                                            <AlertCircleIcon className="font-light" /> Surcharge:{" "}
                                            <span className="font-medium">
                                                {theSelectedCurrency}
                                                20
                                            </span>
                                        </p>
                                    </div> */}
                  <div className="flex items-center gap-4">
                    <button
                      onClick={goBack}
                      type="button"
                      className="border-0 bg-none"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        goSubmitInvoice();
                      }}
                      disabled={!validateForDuplicate}
                      className={` ${!validateForDuplicate
                        ? "bg-[#EBE4FF] text-[#BFA6FF]"
                        : "bg-[#6F00FF] text-[#ffff]"
                        } px-4 py-2 border-0 outline-0 rounded-[8px] flex justify-center items-center text-[16px] leading-6 tracking-[2%] font-medium`}
                    >
                      {loader === true ? <Loader data={loader} /> : "Submit"}{" "}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {showPreview && (
              <PreviewInvoice
                business={business}
                data={{
                  selectedSupplier,
                  issueDate,
                  dueDate,
                  selectedCurrency: currency,
                  items,
                  invoiceDataToEdit: invoice,
                }}
                addTax={addTax}
                user={user}
                itemAmountCalculation={itemAmountCalculation}
                total={total}
                subTotal={subTotal}
                taxedTotal={taxedTotal}
              />
            )}
          </section>
        </div>
        <ConfirmInvoice
          subtotal={secondSubtotal}
          currency={currency}
          show={showModal3}
          loader={loader}
          setloader={setloader}
          setshow={setShowModal3}
          isDraft={isDraft}
          handleSubmitInvoice={() => {
            setloader(true);
            handleSubmitInvoice(isDraft ? submissionType[0] : submissionType[1]);
          }}
          showPreview={true}
        />
        <AddPoPaymentInfoModal
          show={showPaymentInfoModal}
          onClose={() => setShowPaymentInfoModal(false)}
          selectedSupplier={selectedSupplier}
          setSelectedSupplier={setSelectedSupplier}
          selectedBank={selectedBank}
          setSelectedBank={setSelectedBank}
          accountName={accountName}
          setAccountName={setAccountName}
          accountNumber={accountNumber}
          setAccountNumber={setAccountNumber}
          isLoading={isUpdatingUser}
          onSubmit={() =>
            handleUpdateUserDetails({
              ...selectedSupplier,
              paymentInfo: [
                {
                  accountName,
                  accountNumber,
                  bankCode: selectedBank?.value,
                  bankName: selectedBank?.label,
                  currency: userSelectedCurrency,
                },
              ],
            })
          }
        />
      </main>
    </>
  );
};

export default CreateInvoiceV2;
